import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";

import InventoryService from "../../api/inventory";
import IngredientModal from "./modals/IngredientModal";
import ConfirmDeleteModal from "./modals/ConfirmDeleteModal";

const CATEGORY_LABELS = {
  carnes: "Carnes",
  laticinios: "Laticinios",
  hortifruti: "Hortifuti",
  graos: "Graos e Cereais",
  bebidas: "Bebidas",
  temperos: "Temperos",
  embalagens: "Embalagens",
  limpeza: "Limpeza",
  outros: "Outros",
};

const IngredientsPage = ({ showAlert }) => {
  const theme = useTheme();
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [orderBy, setOrderBy] = useState("name");
  const [orderDir, setOrderDir] = useState("asc");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);

  const fetchIngredients = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        page_size: rowsPerPage,
        ordering: orderDir === "desc" ? `-${orderBy}` : orderBy,
      };
      if (search) params.search = search;
      if (categoryFilter) params.category = categoryFilter;

      const res = await InventoryService.getIngredients(params);
      setIngredients(res.data.results || []);
      setTotalCount(res.data.count || 0);
    } catch (err) {
      showAlert("Erro ao carregar ingredientes", "error");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, categoryFilter, orderBy, orderDir, showAlert]);

  useEffect(() => {
    fetchIngredients();
  }, [fetchIngredients]);

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrderDir(orderDir === "asc" ? "desc" : "asc");
    } else {
      setOrderBy(field);
      setOrderDir("asc");
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingItem) {
        await InventoryService.updateIngredient(editingItem.id, data);
        showAlert("Ingrediente atualizado com sucesso!");
      } else {
        await InventoryService.createIngredient(data);
        showAlert("Ingrediente criado com sucesso!");
      }
      setModalOpen(false);
      setEditingItem(null);
      fetchIngredients();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || "Erro ao salvar ingrediente";
      showAlert(msg, "error");
    }
  };

  const handleDelete = async () => {
    try {
      await InventoryService.deleteIngredient(deletingItem.id);
      showAlert("Ingrediente removido com sucesso!");
      setDeleteModalOpen(false);
      setDeletingItem(null);
      fetchIngredients();
    } catch (err) {
      showAlert("Erro ao remover ingrediente", "error");
    }
  };

  const formatNumber = (val) => {
    if (val === null || val === undefined) return "-";
    return Number(val).toLocaleString("pt-BR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    });
  };

  const formatMoney = (val) => {
    if (val === null || val === undefined) return "-";
    return Number(val).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  return (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="h6" fontWeight={700}>
          Ingredientes
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingItem(null);
            setModalOpen(true);
          }}
          sx={{
            backgroundColor: "#2563eb",
            "&:hover": { backgroundColor: "#1d4ed8" },
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Novo Ingrediente
        </Button>
      </Stack>

      {/* Filters */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Buscar ingrediente..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ mr: 1, color: "text.secondary", fontSize: 20 }} />
            ),
          }}
          sx={{ minWidth: 250 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Categoria</InputLabel>
          <Select
            value={categoryFilter}
            label="Categoria"
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">Todas</MenuItem>
            {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: alpha("#2563eb", 0.04) }}>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "name"}
                  direction={orderBy === "name" ? orderDir : "asc"}
                  onClick={() => handleSort("name")}
                >
                  Nome
                </TableSortLabel>
              </TableCell>
              <TableCell>Unidade</TableCell>
              <TableCell>Categoria</TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === "current_quantity"}
                  direction={orderBy === "current_quantity" ? orderDir : "asc"}
                  onClick={() => handleSort("current_quantity")}
                >
                  Qtd. Atual
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Estoque Min.</TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === "current_cost"}
                  direction={orderBy === "current_cost" ? orderDir : "asc"}
                  onClick={() => handleSort("current_cost")}
                >
                  Custo Unit.
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Acoes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  Carregando...
                </TableCell>
              </TableRow>
            ) : ingredients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  Nenhum ingrediente cadastrado.
                </TableCell>
              </TableRow>
            ) : (
              ingredients.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  sx={{
                    backgroundColor: item.is_low_stock
                      ? alpha("#ef4444", 0.04)
                      : "inherit",
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {item.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{item.unit}</TableCell>
                  <TableCell>
                    <Chip
                      label={CATEGORY_LABELS[item.category] || item.category}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    {formatNumber(item.current_quantity)} {item.unit}
                  </TableCell>
                  <TableCell align="right">
                    {item.minimum_stock > 0
                      ? `${formatNumber(item.minimum_stock)} ${item.unit}`
                      : "-"}
                  </TableCell>
                  <TableCell align="right">{formatMoney(item.current_cost)}</TableCell>
                  <TableCell align="center">
                    {item.is_low_stock ? (
                      <Tooltip title="Estoque baixo!">
                        <Chip
                          icon={<WarningIcon sx={{ fontSize: 14 }} />}
                          label="Baixo"
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      </Tooltip>
                    ) : (
                      <Chip label="OK" size="small" color="success" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingItem(item);
                            setModalOpen(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setDeletingItem(item);
                            setDeleteModalOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Linhas por pagina"
        />
      </TableContainer>

      {/* Modals */}
      <IngredientModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingItem(null);
        }}
        onSave={handleSave}
        ingredient={editingItem}
      />

      <ConfirmDeleteModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeletingItem(null);
        }}
        onConfirm={handleDelete}
        title="Excluir Ingrediente"
        message={`Tem certeza que deseja excluir "${deletingItem?.name}"? Esta acao nao pode ser desfeita.`}
      />
    </Box>
  );
};

export default IngredientsPage;
